import { FC } from 'react';

import { PlaceholderBar } from '../../components/loading-skeleton/index';

const StatTilePlaceholder: FC = () => (
  <div className="flex h-20 w-full flex-col justify-center gap-2 rounded-lg bg-gray-50 px-2 py-3 lg:flex-grow lg:basis-[40%]">
    <PlaceholderBar className="h-3 w-24 rounded bg-gray-100" />
    <PlaceholderBar className="h-5 w-16 rounded bg-gray-100" />
  </div>
);

// Ownership Summary — stacked tiles on phone, 2-column wrap from lg.
const OwnershipSummaryPlaceholder: FC = () => (
  <div className="flex w-full flex-grow flex-col gap-3 rounded-lg bg-gray-50 p-1 pt-3 lg:w-[30%] lg:min-w-[350px]">
    <PlaceholderBar className="ml-4 h-3 w-32 rounded bg-gray-100" />
    <div className="flex w-full flex-col gap-3 rounded-md bg-white p-3 shadow-sm lg:flex-row lg:flex-wrap">
      {Array.from({ length: 7 }).map((_, index) => (
        <StatTilePlaceholder key={index} />
      ))}
    </div>
  </div>
);

// Skeleton mirroring the shared components/table Table: h-11 gray header row with one
// px-2 th per column (first column wider — name/share class), divided h-14 body rows.
const TableSkeleton: FC<{ columns: number; rows?: number }> = ({ columns, rows = 2 }) => (
  <table className="h-fit w-full shrink-0 overflow-hidden">
    <thead className="h-11 border-b border-[#F2F2F2] bg-[#F9FAFB]">
      <tr>
        {Array.from({ length: columns }).map((_, index) => (
          <th className="px-2" key={index}>
            <PlaceholderBar
              className={
                index === 0 ? 'h-4 w-24 rounded bg-gray-100' : 'h-4 w-16 rounded bg-gray-100'
              }
            />
          </th>
        ))}
      </tr>
    </thead>
    <tbody className="divide-y divide-[#F2F2F2]">
      {Array.from({ length: rows }).map((_, index) => (
        <tr className="h-14" key={index}>
          {Array.from({ length: columns }).map((_cell, cellIndex) => (
            <td className="px-2" key={cellIndex}>
              <PlaceholderBar
                className={
                  cellIndex === 0 ? 'h-4 w-28 rounded bg-gray-100' : 'h-4 w-12 rounded bg-gray-100'
                }
              />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

// Your Consolidated Position — wide card (~60%) holding the 9-column share-class table
// (share class, issued/diluted/voting %, issued/diluted shares, investment, converted basis, %).
const ConsolidatedPositionPlaceholder: FC = () => (
  <div className="flex flex-grow flex-col gap-3 rounded-lg bg-gray-50 p-[2px] pt-3 lg:basis-[60%]">
    <PlaceholderBar className="ml-4 h-3 w-40 rounded bg-gray-100" />
    <div className="flex h-full w-full flex-wrap gap-3 rounded-md bg-white p-1 shadow-sm">
      <div className="h-full w-full overflow-hidden rounded-md border border-gray-100">
        <TableSkeleton columns={9} rows={2} />
      </div>
    </div>
  </div>
);

// Your Position vs Cap Table — donut + legend.
const DonutCardPlaceholder: FC = () => (
  <div className="flex h-[300px] w-full flex-grow basis-[46%] flex-col gap-3 rounded-lg bg-gray-50 p-1 pt-3">
    <PlaceholderBar className="ml-4 h-3 w-40 rounded bg-gray-100" />
    <div className="flex h-full w-full items-center justify-center gap-8 rounded-md bg-white p-6 shadow-sm">
      <PlaceholderBar className="size-[180px] rounded-full bg-gray-100" />
      <div className="flex flex-col gap-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div className="flex flex-col gap-1" key={index}>
            <PlaceholderBar className="h-3 w-16 rounded bg-gray-100" />
            <PlaceholderBar className="h-3 w-10 rounded bg-gray-100" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Equity & Capital Change — graph block.
const GraphCardPlaceholder: FC = () => (
  <div className="flex h-[300px] w-full flex-grow basis-[46%] flex-col gap-3 rounded-lg bg-gray-50 p-1 pt-3">
    <PlaceholderBar className="ml-4 h-3 w-40 rounded bg-gray-100" />
    <PlaceholderBar className="h-full w-full rounded-md bg-gray-100" />
  </div>
);

// Full-width ledger card (Investment History / SAFE pending conversion) — titled card
// wrapping the shared-Table skeleton with the section's real column count.
const LedgerTablePlaceholder: FC<{ columns: number; rows?: number }> = ({ columns, rows = 2 }) => (
  <div className="flex w-full flex-col gap-3 rounded-lg bg-gray-50 p-1 pt-3">
    <PlaceholderBar className="ml-4 h-3 w-40 rounded bg-gray-100" />
    <div className="flex w-full flex-col gap-1 rounded-md bg-white px-2 pb-2 pt-1 shadow-sm">
      <div className="w-full overflow-hidden rounded-md border border-gray-100">
        <TableSkeleton columns={columns} rows={rows} />
      </div>
    </div>
  </div>
);

// Vesting — two side-by-side tables: About Plan (plan, grant id, plan type, share class)
// and Your Vesting Structure (granted, type, vested, remaining, next/full vesting).
const VestingSectionPlaceholder: FC = () => (
  <div className="flex w-full flex-col gap-3 rounded-lg bg-gray-50 p-1 pt-3">
    <PlaceholderBar className="ml-4 h-3 w-56 rounded bg-gray-100" />
    <div className="flex gap-2 overflow-hidden rounded-md bg-white px-2 shadow-sm">
      <div className="flex h-full w-full flex-col gap-3 rounded-md py-1">
        <PlaceholderBar className="ml-2 mt-3 h-3 w-20 rounded bg-gray-100" />
        <div className="w-full overflow-hidden rounded-md border border-gray-100">
          <TableSkeleton columns={4} rows={2} />
        </div>
      </div>
      <div className="flex h-full w-full flex-col gap-3 rounded-md py-1">
        <PlaceholderBar className="ml-4 mt-3 h-3 w-40 rounded bg-gray-100" />
        <div className="w-full overflow-hidden rounded-md border border-gray-100">
          <TableSkeleton columns={6} rows={2} />
        </div>
      </div>
    </div>
  </div>
);

export const MyHoldingsPlaceholder: FC = () => (
  <div className="flex w-full animate-pulse flex-col gap-4">
    {/* My Holdings / Stakeholders' Holdings tabs */}
    <div className="flex gap-4 border-b border-gray-200 px-4 pb-3">
      <PlaceholderBar className="h-5 w-28 rounded bg-gray-100" />
      <PlaceholderBar className="h-5 w-40 rounded bg-gray-100" />
    </div>

    <div className="flex w-full flex-col gap-4 rounded-lg p-4 shadow-sm">
      <div className="flex w-full flex-wrap items-stretch gap-4">
        <OwnershipSummaryPlaceholder />
        <ConsolidatedPositionPlaceholder />
      </div>
      <div className="flex w-full flex-wrap gap-4">
        <DonutCardPlaceholder />
        <GraphCardPlaceholder />
      </div>
      {/* Investment History — event, date, type, amount, due, share price, shares, %, class, valuation, docs */}
      <LedgerTablePlaceholder columns={11} rows={2} />
      <VestingSectionPlaceholder />
      {/* SAFE pending conversion — investment, due, issue date, cap, discount, floor, maturity, actions */}
      <LedgerTablePlaceholder columns={8} rows={2} />
    </div>
  </div>
);
